import React, {useCallback} from 'react'
import PropTypes from 'prop-types'
import {Stepper, Step, StepLabel} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import './ProgressIndicator.scss'

/**
 * ProgressIndicator component renders a Stepper with specified properties.
 *
 * @param {Object} props - The properties object.
 * @param {number} props.currentStep - The current step to render; 1-based index.
 * @param {boolean} props.isDark - Render in dark mode if true (not used in MUI Stepper).
 * @param {boolean} props.completed - Show the current step as completed if true (not used in MUI Stepper).
 * @param {number} props.steps - The number of steps to render; default is 2 (not used, inferred from titles).
 * @param {Array} props.titles - Step titles; array of strings or objects with title property.
 * @param {string} props.className - Additional class names for styling.
 * @param {string} [props.id=''] - Optional additional id to apply to the progress indicator.
 * @returns {JSX.Element} The rendered Stepper component.
 */

// Custom StepIcon for outlined blue circle when completed
const OutlinedStepIcon = ({active, completed, className, 'data-testid': dataTestId}) => (
  <span className={`progress-step ${className || ''}`.trim()} data-testid={dataTestId}>
    {completed ? (
      <span className="progress-step-icon">
        <CheckIcon className="progress-check" />
      </span>
    ) : (
      <span className={`progress-step-icon ${active ? 'progress-step-active' : 'progress-step-incomplete'}`.trim()}>
        <span className="progress-dot" />
      </span>
    )}
  </span>
)

const NullConnector = () => null

const ProgressIndicator = ({currentStep, titles, className, id, onStepClick}) => {
  // Support both array of strings and array of { title: string }
  const stepLabels = titles.map(t => (typeof t === 'string' ? t : t.title))
  const activeStepIdx = Math.max(0, currentStep - 1)

  const handleStepClick = useCallback(
    (stepIndex) => {
      onStepClick?.(stepIndex + 1)
    },
    [onStepClick]
  )

  return (
    <div className={`progress-indicator ${className || ''}`.trim()} data-testid="progress-indicator-wrapper">
      <Stepper
        activeStep={activeStepIdx}
        alternativeLabel
        className={className}
        data-testid={`progress_indicator${id ? `_${id}` : ''}`}
        id={`progress_indicator${id ? `_${id}` : ''}`}
        connector={<NullConnector />}
      >
        {stepLabels.map((label, stepIndex) => {
          const isCompleted = stepIndex < activeStepIdx
          const isClickable = !!onStepClick && isCompleted
          return (
            <Step key={label || stepIndex} completed={isCompleted}>
              {/* Render connector only for steps after the first */}
              {stepIndex > 0 && (
                <span className={`progress-connector${stepIndex <= activeStepIdx ? ' progress-connector-active' : ''}`} />
              )}
              <StepLabel
                slots={{
                  stepIcon: iconProps => <OutlinedStepIcon {...iconProps} data-testid={`progress-step-icon-${stepIndex}`} />,
                }}
                onClick={
                  isClickable
                    ? e => {
                        e.stopPropagation()
                        handleStepClick(stepIndex)
                      }
                    : undefined
                }
                tabIndex={isClickable ? 0 : -1}
                role={isClickable ? 'button' : undefined}
                aria-label={isClickable ? `Go to step ${stepIndex + 1}` : undefined}
                onKeyDown={
                  isClickable
                    ? e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleStepClick(stepIndex)
                        }
                      }
                    : undefined
                }
              >
                <div className="progress-label-row">
                  <div className="progress-label" data-testid={`progress-step-label-${stepIndex}`}>
                    {label}
                  </div>
                  {typeof titles[stepIndex] === 'object' && titles[stepIndex].description && (
                    <div className="progress-description" data-testid={`progress-step-description-${stepIndex}`}>
                      {titles[stepIndex].description}
                    </div>
                  )}
                </div>
              </StepLabel>
            </Step>
          )
        })}
      </Stepper>
    </div>
  )
}

ProgressIndicator.propTypes = {
  currentStep: PropTypes.number,
  titles: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.shape({title: PropTypes.string.isRequired})])
  ),
  className: PropTypes.string,
  id: PropTypes.string,
  onStepClick: PropTypes.func,
  completed: PropTypes.bool,
  steps: PropTypes.number,
}

ProgressIndicator.defaultProps = {
  currentStep: 1,
  titles: [],
  className: '',
  id: '',
  onStepClick: undefined,
}

export default ProgressIndicator
